/**
 * Cenário do simulador de smart charging (MV2-2006).
 *
 * O cálculo não é feito aqui: o cenário vai para o smartcharging, que roda os mesmos motores
 * (EQUALIZE e PHASE) do grupo de verdade, sem gravar nada e sem enviar comando para estação.
 * Este arquivo só monta, edita e normaliza o cenário.
 */

export type PhaseSystem = 'SINGLE_PHASE' | 'TWO_PHASE' | 'THREE_PHASE';
export type GridPhase = 'A' | 'B' | 'C';

export type SimConnector = {
  key: string;
  connector_id?: string | null;
  seq: number;
  current_max_a: number | null;
  /** Potência máxima cadastrada na estação (kW), só para exibição */
  power_max_kw?: number | null;
  charging: boolean;
  /** Quantas fases o carro puxa, a partir de L1. null = todas as da estação. */
  car_phases: number | null;
};

export type SimChargeBox = {
  key: string;
  charge_box_id?: string | null;
  code: string;
  phase_system: PhaseSystem | null;
  phase: string | null;
  voltage_v: number | null;
  connectors: SimConnector[];
};

export type SimMeter = {
  online: boolean;
  external_load_a: Record<GridPhase, number>;
};

export type SimScenario = {
  current_nominal_a: number;
  /** null = grupo sem medidor */
  meter: SimMeter | null;
  charge_boxes: SimChargeBox[];
};

export type SimConnectorResult = {
  charge_box_id?: string | null;
  code: string;
  connector_id?: string | null;
  seq: number;
  charging: boolean;
  phases: string;
  current_a: number;
  power_kw: number;
};

export type SimPhaseResult = {
  phase: GridPhase;
  connectors_a: number;
  external_a: number;
  used_a: number;
  limit_a: number | null;
  over_limit: boolean | null;
};

export type SimAlgorithmResult = {
  algorithm: 'EQUALIZE' | 'PHASE';
  limit_rule: 'TOTAL' | 'PER_PHASE';
  meter_fail_safe: boolean;
  connectors: SimConnectorResult[];
  phases: SimPhaseResult[];
  limit_a: number | null;
  used_a: number | null;
  over_limit: boolean;
  total_current_a: number;
  total_power_kw: number;
};

export type SimWarning = {
  type: 'CHARGE_BOX_WITHOUT_PHASE' | 'CAR_PHASES_ADJUSTED';
  charge_box_id?: string | null;
  code: string;
};

export type SimResult = {
  security_a: number;
  algorithms: SimAlgorithmResult[];
  warnings: SimWarning[];
};

export const GRID_PHASES: GridPhase[] = ['A', 'B', 'C'];

export const PHASES_BY_SYSTEM: Record<PhaseSystem, string[]> = {
  SINGLE_PHASE: ['A', 'B', 'C'],
  TWO_PHASE: ['AB', 'BA', 'BC', 'CB', 'CA', 'AC'],
  THREE_PHASE: ['ABC', 'ACB', 'BAC', 'BCA', 'CAB', 'CBA'],
};

export const SYSTEM_LABELS: Record<PhaseSystem, string> = {
  SINGLE_PHASE: 'Monofásica',
  TWO_PHASE: 'Bifásica',
  THREE_PHASE: 'Trifásica',
};

/** Rotação usada nas estações novas: espalha as ligações pelas fases, como numa instalação bem feita. */
const ROTATION: Record<PhaseSystem, string[]> = {
  SINGLE_PHASE: ['A', 'B', 'C'],
  TWO_PHASE: ['AB', 'BC', 'CA'],
  THREE_PHASE: ['ABC', 'BCA', 'CAB'],
};

let sequence = 0;
export const newKey = (prefix: string) => {
  sequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${sequence}`;
};

export const defaultVoltage = (system: PhaseSystem | null) =>
  system === 'THREE_PHASE' ? 380 : 220;

/** Quantas fases a estação tem, pela rotação ou pelo sistema. */
export const stationPhaseCount = (chargeBox: SimChargeBox) => {
  if (chargeBox.phase) {
    return chargeBox.phase.length;
  }

  if (chargeBox.phase_system === 'SINGLE_PHASE') return 1;
  if (chargeBox.phase_system === 'TWO_PHASE') return 2;
  return 3;
};

export const newConnector = (seq = 1, currentMaxA = 32): SimConnector => ({
  key: newKey('connector'),
  seq,
  current_max_a: currentMaxA,
  charging: false,
  car_phases: null,
});

export const newChargeBox = (
  index: number,
  system: PhaseSystem = 'THREE_PHASE',
): SimChargeBox => ({
  key: newKey('station'),
  code: `Estação ${index + 1}`,
  phase_system: system,
  phase: ROTATION[system][index % ROTATION[system].length],
  voltage_v: defaultVoltage(system),
  connectors: [newConnector()],
});

export const emptyMeter = (): SimMeter => ({
  online: true,
  external_load_a: { A: 0, B: 0, C: 0 },
});

/**
 * Exemplo da MV2-1895: grupo de 100A, 6 trifásicos instalados em rotação e 4 carros
 * monofásicos. É o cenário em que o Por Fases mais se distancia do Geral.
 */
export const exampleScenario = (): SimScenario => ({
  current_nominal_a: 100,
  meter: null,
  charge_boxes: Array.from({ length: 6 }, (_, index) => {
    const chargeBox = newChargeBox(index);
    chargeBox.connectors[0].charging = index < 4;
    chargeBox.connectors[0].car_phases = index < 4 ? 1 : null;
    return chargeBox;
  }),
});

/** Cenário que veio do backend (grupo existente ou estações do formulário) com as chaves da tela. */
export const withKeys = (scenario: any): SimScenario => ({
  current_nominal_a: Number(scenario?.current_nominal_a) || 0,
  meter: scenario?.meter
    ? {
        online: scenario.meter.online !== false,
        external_load_a: {
          A: Number(scenario.meter.external_load_a?.A) || 0,
          B: Number(scenario.meter.external_load_a?.B) || 0,
          C: Number(scenario.meter.external_load_a?.C) || 0,
        },
      }
    : null,
  charge_boxes: chargeBoxesWithKeys(scenario?.charge_boxes),
});

export const chargeBoxesWithKeys = (chargeBoxes: any[] | undefined): SimChargeBox[] =>
  (chargeBoxes || []).map((chargeBox, index) => ({
    key: newKey('station'),
    charge_box_id: chargeBox.charge_box_id,
    code: chargeBox.code || `Estação ${index + 1}`,
    phase_system: chargeBox.phase_system || null,
    phase: chargeBox.phase || null,
    voltage_v: chargeBox.voltage_v || null,
    connectors: (chargeBox.connectors?.length ? chargeBox.connectors : [{ seq: 1 }]).map(
      (connector: any, connectorIndex: number) => ({
        key: newKey('connector'),
        connector_id: connector.connector_id,
        seq: connector.seq || connectorIndex + 1,
        current_max_a: connector.current_max_a ?? null,
        power_max_kw: connector.power_max_kw ?? null,
        charging: Boolean(connector.charging),
        car_phases: connector.car_phases ?? null,
      }),
    ),
  }));

/** Corpo do POST: sem as chaves de tela. */
export const toRequest = (scenario: SimScenario) => ({
  current_nominal_a: Number(scenario.current_nominal_a) || 0,
  meter: scenario.meter
    ? {
        online: scenario.meter.online,
        external_load_a: { ...scenario.meter.external_load_a },
      }
    : null,
  charge_boxes: scenario.charge_boxes.map((chargeBox) => ({
    charge_box_id: chargeBox.charge_box_id,
    code: chargeBox.code,
    phase_system: chargeBox.phase_system,
    phase: chargeBox.phase,
    voltage_v: chargeBox.voltage_v,
    connectors: chargeBox.connectors.map((connector) => ({
      connector_id: connector.connector_id,
      seq: connector.seq,
      current_max_a: connector.current_max_a,
      charging: connector.charging,
      car_phases: connector.car_phases,
    })),
  })),
});

/** Estações sem fase/rotação cadastrada (aviso do formulário e do simulador). */
export const chargeBoxesWithoutPhase = <T extends { phase?: string | null }>(chargeBoxes: T[] = []) =>
  chargeBoxes.filter((chargeBox) => !chargeBox.phase);
